import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { CheckCircle, Clock, Mail, ChevronRight, Users, RefreshCw } from 'lucide-react';

const Tasks = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [assignedFilter, setAssignedFilter] = useState('me'); // 'me' or 'all'
  const [selectedTask, setSelectedTask] = useState(null);
  const [generatingEmail, setGeneratingEmail] = useState(false);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ status: filter });
      if (assignedFilter === 'me') {
        params.append('assignedTo', 'me');
      }
      const response = await api.get(`/tasks?${params.toString()}`);
      setTasks(response.data.tasks);
    } catch (err) {
      console.error('Failed to fetch tasks:', err);
    } finally {
      setLoading(false);
    }
  }, [filter, assignedFilter]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const handleGenerateEmail = async (taskId) => {
    setGeneratingEmail(true);
    try {
      const response = await api.post(`/tasks/${taskId}/generate-email`);
      setSelectedTask(prev => ({
        ...prev,
        generatedSubject: response.data.subject,
        generatedBody: response.data.body
      }));
    } catch (err) {
      alert('Failed to generate email');
    } finally {
      setGeneratingEmail(false);
    }
  };

  // State for status override
  const [selectedStatusOverride, setSelectedStatusOverride] = useState('');

  const handleCompleteTask = async (taskId, action = 'complete', newStatus = null) => {
    try {
      await api.post(`/tasks/${taskId}/complete`, { action, newStatus });
      fetchTasks();
      setSelectedTask(null);
      setSelectedStatusOverride('');
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to complete task';
      alert(errorMsg);
    }
  };

  // Valid statuses for returning to monitoring
  const monitoringStatuses = [
    { value: 'DAY1_SENT', label: 'Day 1 - Sent (Continue monitoring)' },
    { value: 'DAY2_NO_RESPONSE', label: 'Day 2 - No Response' },
    { value: 'DAY2_SENT', label: 'Day 2 - Sent' },
    { value: 'DAY3_NO_RESPONSE', label: 'Day 3 - No Response' },
    { value: 'DAY3_SENT', label: 'Day 3 - Sent' },
    { value: 'DAY4_ESCALATE', label: 'Day 4 - Escalate' }
  ];

  const getStatusBadge = (status) => {
    const colors = {
      pending: 'warning',
      approved: 'info',
      completed: 'success',
      cancelled: 'secondary'
    };
    return colors[status] || 'secondary';
  };

  // Helper to get PO info from task (fallback to ai_extracted_data)
  const getPoInfo = (task) => {
    if (task.poLine) {
      return {
        poNumber: task.poLine.po_number,
        lineNumber: task.poLine.line_number,
        supplierName: task.poLine.supplier_name || task.poLine.supplier?.supplier_name,
        itemNumber: task.poLine.item_number
      };
    }
    // Fallback to stored data
    const extracted = task.ai_extracted_data || {};
    return {
      poNumber: extracted.poNumber || 'N/A',
      lineNumber: extracted.lineNumber || '-',
      supplierName: extracted.supplierName || extracted.emailFrom || 'Unknown',
      itemNumber: extracted.itemNumber || '-'
    };
  };

  // Tab descriptions
  const tabDescriptions = {
    pending: 'Tasks awaiting action - generate emails, review responses, or update status.',
    approved: 'Approved tasks ready to be sent or processed.',
    completed: 'Completed tasks - historical record of actions taken.'
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 style={{ fontSize: '1.5rem', fontWeight: '600' }}>Tasks</h1>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-4 mb-4 flex-wrap">
        <div className="flex gap-2">
          {['pending', 'approved', 'completed'].map((status) => (
            <button
              key={status}
              className={`btn ${filter === status ? 'btn-primary' : 'btn-outline'}`}
              onClick={() => setFilter(status)}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>
        <div className="flex gap-2" style={{ borderLeft: '1px solid #ddd', paddingLeft: '16px' }}>
          <button
            className={`btn ${assignedFilter === 'me' ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => setAssignedFilter('me')}
          >
            My Tasks
          </button>
          <button
            className={`btn ${assignedFilter === 'all' ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => setAssignedFilter('all')}
          >
            <Users size={16} style={{ marginRight: '4px' }} /> All Tasks
          </button>
        </div>
      </div>

      {/* Tab Description */}
      <div style={{ 
        padding: '12px 16px', 
        background: '#f8fafc', 
        borderRadius: '8px', 
        marginBottom: '16px',
        fontSize: '14px',
        color: '#64748b'
      }}>
        {tabDescriptions[filter]}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: selectedTask ? '1fr 400px' : '1fr', gap: '24px' }}>
        {/* Task List */}
        <div className="card">
          {loading ? (
            <div className="loading"><div className="spinner" /></div>
          ) : tasks.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">✓</div>
              <p>No {filter} tasks</p>
            </div>
          ) : (
            <div>
              {tasks.map((task) => {
                const poInfo = getPoInfo(task);
                return (
                <div
                  key={task.task_id}
                  onClick={() => setSelectedTask(task)}
                  style={{
                    padding: '16px 20px',
                    borderBottom: '1px solid var(--border)',
                    cursor: 'pointer',
                    background: selectedTask?.task_id === task.task_id ? 'var(--bg-secondary)' : 'transparent'
                  }}
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="font-medium">
                        PO {poInfo.poNumber} - Line {poInfo.lineNumber}
                      </div>
                      <div className="text-sm text-muted">
                        {poInfo.supplierName} • {task.task_type}
                        {task.ai_suggested_action && (
                          <span style={{ 
                            marginLeft: '8px',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: '500',
                            background: task.ai_suggested_action === 'POSITIVE' ? '#dcfce7' :
                                       task.ai_suggested_action === 'DELAY' ? '#fef3c7' :
                                       task.ai_suggested_action === 'SHORT_CLOSE' ? '#fee2e2' :
                                       task.ai_suggested_action === 'NEEDS_REVIEW' ? '#dbeafe' : '#f3f4f6',
                            color: task.ai_suggested_action === 'POSITIVE' ? '#166534' :
                                  task.ai_suggested_action === 'DELAY' ? '#92400e' :
                                  task.ai_suggested_action === 'SHORT_CLOSE' ? '#991b1b' :
                                  task.ai_suggested_action === 'NEEDS_REVIEW' ? '#1e40af' : '#374151'
                          }}>
                            {task.ai_suggested_action}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`badge badge-${getStatusBadge(task.status)}`}>
                        {task.status}
                      </span>
                      <ChevronRight size={16} />
                    </div>
                  </div>
                </div>
              )})}
            </div>
          )}
        </div>

        {/* Task Detail */}
        {selectedTask && (() => {
          const selectedPoInfo = getPoInfo(selectedTask);
          const isResponseReview = selectedTask.task_type === 'RESPONSE_REVIEW';
          const extractedData = selectedTask.ai_extracted_data || {};
          
          return (
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Task Details</h3>
              <button className="btn btn-outline btn-sm" onClick={() => { setSelectedTask(null); setSelectedStatusOverride(''); }}>
                Close
              </button>
            </div>
            <div className="card-body">
              <div className="mb-4">
                <div className="text-sm text-muted">PO Number</div>
                <div className="font-medium">{selectedPoInfo.poNumber}</div>
              </div>
              
              <div className="mb-4">
                <div className="text-sm text-muted">Supplier</div>
                <div>{selectedPoInfo.supplierName}</div>
              </div>
              
              <div className="mb-4">
                <div className="text-sm text-muted">Item</div>
                <div>{selectedPoInfo.itemNumber}</div>
              </div>
              
              <div className="mb-4">
                <div className="text-sm text-muted">Task Type</div>
                <div>{selectedTask.task_type}</div>
              </div>

              {/* Response Review specific info */}
              {isResponseReview && (
                <>
                  {/* Original Request Type Context */}
                  {extractedData.requestType && (
                    <div className="mb-4">
                      <div className="text-sm text-muted">Original Request</div>
                      <div style={{ 
                        display: 'inline-block',
                        padding: '4px 12px',
                        borderRadius: '4px',
                        fontSize: '13px',
                        background: '#f1f5f9',
                        color: '#475569'
                      }}>
                        {extractedData.requestType === 'EXPEDITE' && '⚡ Expedite - We asked for EARLIER shipment'}
                        {extractedData.requestType === 'DE_EXPEDITE' && '⏸️ De-Expedite - We asked to DELAY shipment'}
                        {extractedData.requestType === 'PUSH' && '⏸️ Push - We asked to DELAY shipment'}
                        {extractedData.requestType === 'PAST_DUE' && `⏰ Past Due Follow-up (${extractedData.daysPastDue || 0} days overdue)`}
                        {extractedData.requestType === 'GENERAL' && '📋 General Status Request'}
                        {!['EXPEDITE', 'DE_EXPEDITE', 'PUSH', 'PAST_DUE', 'GENERAL'].includes(extractedData.requestType) && `📋 ${extractedData.requestType}`}
                      </div>
                    </div>
                  )}

                  {/* Supplier Response Classification */}
                  {extractedData.responseType && (
                    <div className="mb-4">
                      <div className="text-sm text-muted">Supplier Response</div>
                      <div className="flex items-center gap-2">
                        <span style={{ 
                          display: 'inline-block',
                          padding: '4px 12px',
                          borderRadius: '4px',
                          fontWeight: '500',
                          background: extractedData.responseType === 'POSITIVE' ? '#dcfce7' :
                                     extractedData.responseType === 'DELAY' ? '#fef3c7' :
                                     extractedData.responseType === 'SHORT_CLOSE' ? '#fee2e2' :
                                     extractedData.responseType === 'NEEDS_REVIEW' ? '#dbeafe' : '#f3f4f6',
                          color: extractedData.responseType === 'POSITIVE' ? '#166534' :
                                extractedData.responseType === 'DELAY' ? '#92400e' :
                                extractedData.responseType === 'SHORT_CLOSE' ? '#991b1b' :
                                extractedData.responseType === 'NEEDS_REVIEW' ? '#1e40af' : '#374151'
                        }}>
                          {extractedData.responseType}
                          {extractedData.supplierAgreesToRequest === true && ' ✓'}
                        </span>
                        {extractedData.responseConfidence && (
                          <span className="text-sm text-muted">
                            ({Math.round(extractedData.responseConfidence * 100)}% confidence)
                          </span>
                        )}
                      </div>
                      {extractedData.dateComparison && extractedData.dateComparison !== 'UNKNOWN' && (
                        <div className="text-sm mt-1" style={{ color: '#64748b' }}>
                          {extractedData.dateComparison === 'EARLIER' && '📅 New date is EARLIER than expected'}
                          {extractedData.dateComparison === 'SAME' && '📅 New date is same as expected'}
                          {extractedData.dateComparison === 'LATER' && '📅 New date is LATER than expected'}
                        </div>
                      )}
                    </div>
                  )}

                  {/* AI Summary / Message Gist */}
                  {extractedData.messageGist && (
                    <div className="mb-4">
                      <div className="text-sm text-muted">AI Summary</div>
                      <div style={{ 
                        background: '#f0f9ff', 
                        padding: '12px', 
                        borderRadius: 'var(--radius)',
                        borderLeft: '3px solid #0ea5e9',
                        fontSize: '14px'
                      }}>
                        {extractedData.messageGist}
                      </div>
                    </div>
                  )}

                  {/* New Ship Date from AI */}
                  {extractedData.newShipDate && (
                    <div className="mb-4">
                      <div className="text-sm text-muted">New Ship Date (from response)</div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">
                          {new Date(extractedData.newShipDate).toLocaleDateString()}
                        </span>
                        {extractedData.isDateEarlier && (
                          <span style={{ color: '#16a34a', fontSize: '12px' }}>✓ Earlier than current ESD</span>
                        )}
                        {extractedData.meetsRequestedDate && (
                          <span style={{ color: '#16a34a', fontSize: '12px' }}>✓ Meets requested date</span>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {extractedData.trackingNumber && (
                    <div className="mb-4">
                      <div className="text-sm text-muted">Tracking Number</div>
                      <div className="font-medium">{extractedData.trackingNumber}</div>
                    </div>
                  )}

                  {/* Date Context */}
                  {(extractedData.originalESD || extractedData.currentESD || extractedData.requiredDate) && (
                    <div className="mb-4">
                      <div className="text-sm text-muted mb-2">Date Context</div>
                      <div style={{ fontSize: '13px', display: 'grid', gap: '4px' }}>
                        {extractedData.originalESD && (
                          <div>Original ESD: <span className="font-medium">{new Date(extractedData.originalESD).toLocaleDateString()}</span></div>
                        )}
                        {extractedData.currentESD && (
                          <div>Current ESD: <span className="font-medium">{new Date(extractedData.currentESD).toLocaleDateString()}</span></div>
                        )}
                        {extractedData.requiredDate && (
                          <div>Requested Date: <span className="font-medium">{new Date(extractedData.requiredDate).toLocaleDateString()}</span></div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Reason / Explanation */}
                  {extractedData.responseReason && (
                    <div className="mb-4">
                      <div className="text-sm text-muted">Analysis</div>
                      <div className="text-sm" style={{ color: '#64748b' }}>
                        {extractedData.responseReason}
                      </div>
                    </div>
                  )}

                  {/* Original Email Subject */}
                  {extractedData.emailSubject && (
                    <div className="mb-4">
                      <div className="text-sm text-muted">Email Subject</div>
                      <div className="text-sm">{extractedData.emailSubject}</div>
                    </div>
                  )}
                </>
              )}

              <hr style={{ margin: '16px 0', border: 'none', borderTop: '1px solid var(--border)' }} />

              {/* Actions */}
              {selectedTask.status === 'pending' && (
                <div>
                  {/* EMAIL_PENDING tasks - email already generated, need confirmation */}
                  {selectedTask.task_type === 'EMAIL_PENDING' ? (
                    <>
                      <div className="mb-4">
                        <div className="text-sm text-muted mb-2">Email Ready to Send</div>
                        <div style={{ background: 'var(--bg-secondary)', padding: '12px', borderRadius: 'var(--radius)' }}>
                          <div className="font-medium mb-2">{selectedTask.generated_email_subject}</div>
                          <div className="text-sm">{selectedTask.generated_email_body}</div>
                        </div>
                      </div>
                      <button
                        className="btn btn-primary w-full mb-2"
                        onClick={() => handleCompleteTask(selectedTask.task_id, 'email_sent', 'DAY1_SENT')}
                      >
                        <CheckCircle size={16} />
                        Confirm Email Sent
                      </button>
                      <button
                        className="btn btn-outline w-full"
                        onClick={() => handleCompleteTask(selectedTask.task_id, 'skip')}
                      >
                        Skip This Item
                      </button>
                    </>
                  ) : !isResponseReview ? (
                    <>
                      <button
                        className="btn btn-primary w-full mb-4"
                        onClick={() => handleGenerateEmail(selectedTask.task_id)}
                        disabled={generatingEmail}
                      >
                        <Mail size={16} />
                        {generatingEmail ? 'Generating...' : 'Generate Email'}
                      </button>

                      {selectedTask.generatedSubject && (
                        <div className="mb-4">
                          <div className="text-sm text-muted mb-2">Generated Email</div>
                          <div style={{ background: 'var(--bg-secondary)', padding: '12px', borderRadius: 'var(--radius)' }}>
                            <div className="font-medium mb-2">{selectedTask.generatedSubject}</div>
                            <div className="text-sm" dangerouslySetInnerHTML={{ __html: selectedTask.generatedBody }} />
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="mb-4 text-sm text-muted">
                      Review the supplier response above and update the PO status accordingly.
                    </div>
                  )}

                  {/* Action buttons for RESPONSE_REVIEW tasks */}
                  {isResponseReview ? (
                    <div style={{ marginTop: '16px' }}>
                      {/* Complete button - Primary action */}
                      <button
                        className="btn btn-primary w-full"
                        onClick={() => handleCompleteTask(selectedTask.task_id, 'complete')}
                        style={{ 
                          padding: '14px 20px',
                          fontSize: '0.95rem',
                          fontWeight: '600',
                          borderRadius: '8px',
                          boxShadow: '0 2px 8px rgba(247, 89, 0, 0.25)'
                        }}
                      >
                        <CheckCircle size={18} />
                        Accept & Complete
                      </button>

                      {/* OR divider */}
                      <div className="flex items-center gap-3" style={{ margin: '20px 0' }}>
                        <div style={{ flex: 1, height: '1px', background: 'linear-gradient(to right, transparent, var(--border), transparent)' }} />
                        <span className="text-sm text-muted" style={{ fontWeight: '500', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.5px' }}>or return to monitoring</span>
                        <div style={{ flex: 1, height: '1px', background: 'linear-gradient(to left, transparent, var(--border), transparent)' }} />
                      </div>

                      {/* Status override section */}
                      <div style={{ 
                        background: 'var(--bg-secondary)', 
                        borderRadius: '10px', 
                        padding: '16px',
                        border: '1px solid var(--border)'
                      }}>
                        <label className="text-sm font-medium" style={{ display: 'block', marginBottom: '10px', color: 'var(--text-secondary)' }}>
                          Set status to continue SOP monitoring
                        </label>
                        <select
                          className="form-control"
                          value={selectedStatusOverride}
                          onChange={(e) => setSelectedStatusOverride(e.target.value)}
                          style={{ 
                            padding: '12px 14px',
                            fontSize: '0.9rem',
                            borderRadius: '8px',
                            border: '2px solid var(--border)',
                            background: 'var(--bg-primary)',
                            cursor: 'pointer',
                            transition: 'border-color 0.2s, box-shadow 0.2s'
                          }}
                        >
                          <option value="">Select new status...</option>
                          {monitoringStatuses.map(s => (
                            <option key={s.value} value={s.value}>{s.label}</option>
                          ))}
                        </select>

                        {selectedStatusOverride && (
                          <button
                            className="btn w-full"
                            onClick={() => handleCompleteTask(selectedTask.task_id, 'set_status', selectedStatusOverride)}
                            style={{ 
                              marginTop: '12px',
                              padding: '12px 20px',
                              fontSize: '0.9rem',
                              fontWeight: '600',
                              borderRadius: '8px',
                              background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                              color: 'white',
                              border: 'none',
                              boxShadow: '0 2px 8px rgba(99, 102, 241, 0.3)',
                              transition: 'transform 0.2s, box-shadow 0.2s'
                            }}
                          >
                            <RefreshCw size={16} />
                            Set Status & Close Task
                          </button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <button
                      className="btn btn-secondary w-full"
                      onClick={() => handleCompleteTask(selectedTask.task_id, 'complete')}
                      style={{ 
                        padding: '12px 20px',
                        fontSize: '0.9rem',
                        fontWeight: '600',
                        borderRadius: '8px'
                      }}
                    >
                      <CheckCircle size={16} />
                      Mark Complete
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        );
        })()}
      </div>
    </div>
  );
};

export default Tasks;